import { useState, useEffect } from 'react'
import { Loader2, RefreshCw, AlertCircle } from 'lucide-react'
import { competitorMapApi } from '@/lib/api/client'

// ============================================================================
// TYPES
// ============================================================================

interface GapItem {
  label: string
  competitors: string[]
  supportingQuote: string
}

interface PositioningGapRadarProps {
  accountId: string
  projectId: string
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PositioningGapRadar({ accountId, projectId }: PositioningGapRadarProps) {
  const [gaps, setGaps] = useState<GapItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function fetchGaps() {
    setIsLoading(true)
    setError(null)
    try {
      const result = await competitorMapApi.getGaps(accountId, projectId) as { gaps: GapItem[] }
      setGaps(result.gaps ?? [])
    } catch {
      setError('Failed to load positioning gaps')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (accountId && projectId) {
      fetchGaps()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, projectId])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '12px', color: 'var(--color-text-muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ fontSize: '13px' }}>Analyzing your product graph…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '12px', color: 'var(--color-text-muted)' }}>
        <AlertCircle size={22} style={{ color: '#dc3545' }} />
        <p style={{ fontSize: '13px', color: '#dc3545' }}>{error}</p>
        <button
          onClick={fetchGaps}
          style={{ padding: '6px 14px', background: 'none', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <RefreshCw size={13} />
          Retry
        </button>
      </div>
    )
  }

  if (gaps.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '10px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '0 40px' }}>
        <p style={{ fontSize: '14px', lineHeight: 1.6 }}>
          No gaps detected — your product covers all identified competitor capabilities.
        </p>
        <button
          onClick={fetchGaps}
          style={{ padding: '6px 14px', background: 'none', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>
    )
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--color-text)', fontFamily: 'var(--font-display)', margin: 0 }}>
            Positioning Gaps
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
            Capabilities shared by 2+ competitors — potential areas to address
          </p>
        </div>
        <button
          onClick={fetchGaps}
          style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '5px 10px', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' }}
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {gaps.map((gap, i) => (
          <GapCard key={i} gap={gap} />
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// GAP CARD
// ============================================================================

function GapCard({ gap }: { gap: GapItem }) {
  const maxBars = 5
  const strength = Math.min(gap.competitors.length, maxBars)

  return (
    <div style={{
      background: 'white',
      border: '1px solid var(--color-border)',
      borderRadius: '10px',
      padding: '14px 16px',
      boxShadow: '0 1px 3px rgba(29,26,21,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
        <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text)', margin: 0 }}>
          {gap.label}
        </p>
        <div style={{ display: 'flex', gap: '3px', flexShrink: 0, marginTop: '2px' }}>
          {Array.from({ length: maxBars }).map((_, i) => (
            <div
              key={i}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '2px',
                background: i < strength ? 'var(--color-accent)' : 'var(--color-warm-200)',
              }}
            />
          ))}
        </div>
      </div>

      <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: '0 0 6px' }}>
        Found in: {gap.competitors.join(', ')}
      </p>

      {gap.supportingQuote && (
        <p style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          fontStyle: 'italic',
          margin: 0,
          paddingLeft: '10px',
          borderLeft: '2px solid var(--color-border-subtle)',
        }}>
          "{gap.supportingQuote}"
        </p>
      )}
    </div>
  )
}
